import { IAccessoriesCarousel } from '@models/IAccessoriesCarousel';
import AccessoryTypeEnum from '@models/enums/AccessoryTypeEnum';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import useProductPageContentStore from '@state/ProductPageStore';
import slugify from '@utils/helpers/slugify';
import { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import AccessoriesCarouselCard from './AccessoriesCarouselCard';

/**
 * Renders a carousel of accessory items related to a product, such as
 * "Included Accessories" or "Optional Accessories".
 *
 * The component determines its data source dynamically based on the `accessoryType` prop.
 * If store data is available (from `useProductPageContentStore`), it will override the
 * fallback props (`items` and `title`).
 */

const AccessoriesCarousel = ({
  title = '',
  items = [],
  anchorId = '',
  accessoryType,
}: IAccessoriesCarousel) => {
  const [accessoryTitle, setAccessoryTitle] = useState(title);
  const [accessoryItems, setAccessoryItems] = useState(items);
  const { productPageContent } = useProductPageContentStore();
  const { includedAccessories, optionalAccessories } = productPageContent;

  useEffect(() => {
    if (
      accessoryType === AccessoryTypeEnum.IncludedAccessories &&
      typeof includedAccessories !== 'undefined' // Data returns null if they're not added to the page
    ) {
      setAccessoryItems(includedAccessories?.items ?? []);
      setAccessoryTitle(includedAccessories?.title ?? title);
      return;
    }

    if (
      accessoryType === AccessoryTypeEnum.OptionalAccessories &&
      typeof optionalAccessories !== 'undefined' // Data returns null if they're not added to the page
    ) {
      setAccessoryItems(optionalAccessories?.items ?? []);
      setAccessoryTitle(optionalAccessories?.title ?? title);
      return;
    }

    setAccessoryItems(items);
    setAccessoryTitle(title);
  }, [accessoryType, includedAccessories, optionalAccessories, items, title]);

  if (!accessoryItems?.length) return null;

  return (
    <section
      id={`a-${anchorId}`}
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-18-Accessories-Carousel"
    >
      <NavigationCarousel
        id={`accessories-carousel-${anchorId}`}
        shadows
        items={accessoryItems}
        slidesToShowDesktop={6}
        slidesToShowTablet={3}
        slidesToShowMobile={1.3}
        title={accessoryTitle}
      >
        {(itemsToDisplay) =>
          itemsToDisplay
            .filter((item) => !!item.name)
            .map((item, index) => (
              <SwiperSlide
                key={slugify(`accessory-${item.name}-${index}`)}
                className="stretch-card"
              >
                <AccessoriesCarouselCard
                  {...item}
                  accessoryType={accessoryType}
                  dataTestId={`CT-18-Accessories-Carousel-Card-${index}`}
                />
              </SwiperSlide>
            ))
        }
      </NavigationCarousel>
    </section>
  );
};

export default AccessoriesCarousel;
